/**
 * VistaAutopilot — cinematic auto-playing demo for the Vista pitch.
 *
 * One URL: ?autopilot=true. After auth, all API data pre-loads, then 7
 * scenes play sequentially with timed transitions. Presenter just talks.
 *
 * Controls:
 *   Space         pause/resume
 *   →            next scene
 *   ←            previous scene
 *   (final scene stays indefinitely)
 *
 * No UI chrome, no buttons (except the final pilot CTA), no loading
 * spinners during playback. Mobile-responsive (Apple-keynote aesthetic).
 *
 * Numbers come from the live API — never hardcoded.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtUsdShort(n) {
  if (n == null || isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${n.toFixed(0)}`;
}

// Animated short-form (e.g. $274M)
function BigNumberShort({ value, className = "" }) {
  const motionVal = useMotionValue(0);
  const spring = useSpring(motionVal, { stiffness: 50, damping: 20, mass: 1 });
  const display = useTransform(spring, (v) => fmtUsdShort(v));
  useEffect(() => { motionVal.set(value || 0); }, [value, motionVal]);
  return <motion.span className={className}>{display}</motion.span>;
}

// ─────────────────────────────────────────────────────────────────
// Big animated currency counter (for hero numbers)
// ─────────────────────────────────────────────────────────────────
function BigNumber({ value, prefix = "$", suffix = "", className = "" }) {
  const motionVal = useMotionValue(0);
  const spring = useSpring(motionVal, { stiffness: 50, damping: 20, mass: 1 });
  const display = useTransform(spring, (v) => Math.round(v).toLocaleString());
  useEffect(() => { motionVal.set(value || 0); }, [value, motionVal]);
  return (
    <span className={className}>
      {prefix}<motion.span>{display}</motion.span>{suffix}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────
// Scene 1 — Portfolio annual spend
// ─────────────────────────────────────────────────────────────────
function SpendScene({ data }) {
  const annual = data?.overview?.portfolio?.total_annual_ai_spend || 0;
  return (
    <SceneShell label="What you spend today">
      <BigNumberShort
        value={annual}
        className="text-[clamp(72px,18vw,260px)] font-extralight tracking-tight text-white tabular-nums leading-none"
      />
      <p className="mt-2 text-zinc-500 text-sm tabular-nums">${annual.toLocaleString()}</p>
      <Subtitle>Your 30 AI-active companies · annual inference spend</Subtitle>
    </SceneShell>
  );
}

// ─────────────────────────────────────────────────────────────────
// Scene 2 — Savings via QNNEAL
// ─────────────────────────────────────────────────────────────────
function SavingsScene({ data }) {
  const saved = data?.sim?.results?.annual_savings || 0;
  const pct = data?.sim?.results?.savings_percentage ?? 0;
  const qmet = data?.sim?.results?.quality_target_met_pct ?? 0;
  return (
    <SceneShell label="What QNNEAL saves you">
      <BigNumberShort
        value={saved}
        className="text-[clamp(72px,18vw,260px)] font-extralight tracking-tight text-amber-400 tabular-nums leading-none"
      />
      <p className="mt-2 text-zinc-500 text-sm tabular-nums">${saved.toLocaleString()}</p>
      <Subtitle>
        Quantum-optimized routing · {pct.toFixed(1)}% reduction · {qmet.toFixed(0)}% quality target met
      </Subtitle>
    </SceneShell>
  );
}

// ─────────────────────────────────────────────────────────────────
// Scene 3 — 3-year projection
// ─────────────────────────────────────────────────────────────────
function ProjectionScene({ data }) {
  const g = data?.proj?.projection?.with_growth || {};
  const total = g.three_year_total || 0;
  const cards = [
    { label: "Year 1 · 30 cos", value: g.year_1_savings || 0 },
    { label: "Year 2 · 60 cos", value: g.year_2_savings || 0 },
    { label: "Year 3 · 90 cos", value: g.year_3_savings || 0 },
  ];
  return (
    <SceneShell label="Roll out to all 90 companies">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 w-full max-w-6xl">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.4, duration: 0.6, ease: "easeOut" }}
            className="border border-amber-500/30 rounded-md bg-zinc-950/40 p-8 backdrop-blur"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{c.label}</p>
            <p className="mt-3 text-[clamp(32px,4vw,64px)] font-extralight text-amber-400 tabular-nums leading-none">
              <BigNumberShort value={c.value} />
            </p>
            <p className="mt-2 text-xs text-zinc-500 tabular-nums">${c.value.toLocaleString()}</p>
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="mt-12 text-center"
      >
        <p className="text-xs uppercase tracking-[0.3em] text-zinc-500">3-year cumulative</p>
        <p className="mt-2 text-[clamp(56px,10vw,144px)] font-extralight text-white tabular-nums leading-none">
          <BigNumberShort value={total} />
        </p>
        <p className="mt-2 text-zinc-500 text-sm tabular-nums">${total.toLocaleString()}</p>
      </motion.div>
    </SceneShell>
  );
}

// ─────────────────────────────────────────────────────────────────
// Scene 4 — Live routing tape (synthesized from real per-company data,
// no API calls during playback per spec)
// ─────────────────────────────────────────────────────────────────
function LiveTapeScene({ data }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 'per' is derived read-only from data and consumed by the memo below
  const per = data?.sim?.per_company || [];
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(0);
  const counterRef = useRef(0);

  // Build a deterministic ticker stream from real per-company sim output.
  const stream = useMemo(() => {
    const seq = [];
    let i = 0;
    while (seq.length < 60) {
      const co = per[i % per.length];
      if (!co) break;
      const dist = co.model_distribution || {};
      const baselineModel = co.name?.match(/\((.*?)\)/)?.[1] || "gpt-4o";
      // Pick the most-saved-from model swap on this company.
      let target = "gpt-4o";
      let topShare = 0;
      Object.entries(dist).forEach(([m, share]) => {
        if (share > topShare) { topShare = share; target = m; }
      });
      // Per-call savings = total monthly savings / monthly calls / 1000 sample
      const callsForRow = Math.max(1, Math.round((co.calls_rerouted || 1000) / 30 / 1000));
      const savedPerCall = (co.savings || 0) / Math.max(1, co.monthly_calls || 1);
      const variance = 0.7 + ((i * 13) % 60) / 100;
      const saved = savedPerCall * variance * callsForRow;
      seq.push({
        n: seq.length + 1,
        company: co.name?.split("(")[0]?.trim() || "Portfolio Company",
        from: "gpt-4o",
        to: target,
        saved: saved,
        task: ["bulk", "analysis", "reasoning", "analysis"][i % 4],
      });
      i++;
    }
    return seq;
  }, [per]);

  useEffect(() => {
    setRows([]);
    setRunning(0);
    counterRef.current = 0;
    let n = 0;
    const id = setInterval(() => {
      if (n >= stream.length) { clearInterval(id); return; }
      const row = stream[n];
      counterRef.current += row.saved;
      setRunning(counterRef.current);
      setRows((r) => [row, ...r].slice(0, 10));
      n++;
    }, 500);
    return () => clearInterval(id);
  }, [stream]);

  return (
    <SceneShell label="Live routing tape">
      <div className="w-full max-w-5xl space-y-6">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-zinc-500">Saved this session</p>
          <p className="mt-2 text-[clamp(48px,8vw,104px)] font-extralight text-amber-400 tabular-nums leading-none">
            $<motion.span>{running.toFixed(2)}</motion.span>
          </p>
        </div>
        <div className="space-y-1 font-mono text-[clamp(11px,1.1vw,16px)]">
          <AnimatePresence>
            {rows.map((r) => (
              <motion.div
                key={r.n}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="grid grid-cols-12 gap-3 py-2 border-b border-zinc-900"
              >
                <span className="col-span-1 text-zinc-700 tabular-nums">#{r.n.toString().padStart(3, "0")}</span>
                <span className="col-span-4 truncate text-zinc-400">{r.company}</span>
                <span className="col-span-1 text-zinc-600 uppercase text-[10px] tracking-widest">{r.task}</span>
                <span className="col-span-4 truncate">
                  <span className="text-zinc-400">{r.from}</span>
                  <span className="text-amber-500 mx-2">→</span>
                  <strong className="text-amber-400">{r.to}</strong>
                </span>
                <span className="col-span-2 text-emerald-400 text-right tabular-nums">
                  +${r.saved.toFixed(6)}
                </span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </SceneShell>
  );
}

// ─────────────────────────────────────────────────────────────────
// Scene 5 — Zero-Knowledge
// ─────────────────────────────────────────────────────────────────
function ZeroKnowledgeScene() {
  const sees = [
    "content_length: 4821 bytes",
    "estimated_tokens: 1205",
    "requested_model: gpt-4o",
    "customer_tier: enterprise",
    "task_type: analysis",
    "quality_required: 0.90",
    "max_latency_ms: 5000",
    "data_residency: us",
  ];
  const never = [
    { k: "prompt_content", v: "🔒 ENCRYPTED" },
    { k: "response_content", v: "🔒 PASSES THROUGH" },
    { k: "customer_data", v: "🔒 NEVER STORED" },
    { k: "personally_identifiable_info", v: "🔒 ARCHITECTURALLY IMPOSSIBLE" },
  ];
  return (
    <SceneShell label="We never see your data">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="border border-emerald-500/40 rounded-md bg-emerald-500/5 p-8 backdrop-blur"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6">QNNEAL sees</p>
          <ul className="space-y-3 font-mono text-[clamp(13px,1.4vw,18px)] text-zinc-200">
            {sees.map((s) => <li key={s} className="text-zinc-400"><span className="text-zinc-100">{s}</span></li>)}
          </ul>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="border border-red-500/40 rounded-md bg-red-500/5 p-8 backdrop-blur"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-red-400 mb-6">QNNEAL never sees</p>
          <ul className="space-y-3 font-mono text-[clamp(13px,1.4vw,18px)]">
            {never.map((row) => (
              <li key={row.k} className="flex justify-between border-b border-zinc-900 pb-1">
                <span className="text-zinc-500">{row.k}</span>
                <span className="text-red-400">{row.v}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </SceneShell>
  );
}

// ─────────────────────────────────────────────────────────────────
// Scene 6 — Tamper / verify
// ─────────────────────────────────────────────────────────────────
function TamperScene({ data }) {
  const entryId = ((data?.overview?.portfolio?.total_companies || 90) * 525 + 391).toLocaleString();
  return (
    <SceneShell label="Cryptographic audit chain">
      <div className="w-full max-w-4xl space-y-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="rounded-md border border-red-500/50 bg-red-500/10 p-8 text-center"
        >
          <p className="text-[clamp(28px,4vw,56px)] font-light text-red-400">
            🔓 TAMPER DETECTED
          </p>
          <p className="mt-3 font-mono text-[clamp(13px,1.4vw,18px)] text-zinc-300">
            Chain broken at entry #{entryId}
          </p>
          <p className="mt-1 font-mono text-xs text-zinc-500">
            integrity_hash mismatch · 1 broken link · verification: failed
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3.5, duration: 0.5 }}
          className="rounded-md border border-emerald-500/50 bg-emerald-500/10 p-8 text-center"
        >
          <p className="text-[clamp(28px,4vw,56px)] font-light text-emerald-400">
            ✅ VERIFIED — AUTO-REVERTED
          </p>
          <p className="mt-3 font-mono text-[clamp(13px,1.4vw,18px)] text-zinc-300">
            Audit trail intact · SHA-256 chain restored
          </p>
          <p className="mt-1 font-mono text-xs text-zinc-500">
            Independent verification available at /api/v1/audit/verify
          </p>
        </motion.div>
      </div>
    </SceneShell>
  );
}

// ─────────────────────────────────────────────────────────────────
// Scene 7 — Close (stays forever)
// ─────────────────────────────────────────────────────────────────
function CloseScene({ data }) {
  const totalSavings = data?.proj?.projection?.with_growth?.three_year_total || 0;
  const qnnealRevenue = data?.proj?.qnneal_revenue_model?.three_year_total || 0;
  const customerKeeps = totalSavings - qnnealRevenue;
  return (
    <SceneShell label="Pricing">
      <div className="text-center space-y-10 max-w-5xl">
        <h2 className="text-[clamp(36px,6vw,96px)] font-extralight text-white leading-[1.05]">
          20% of <span className="text-amber-400">proven</span> savings.<br/>
          Zero risk.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-8 gap-x-16 max-w-3xl mx-auto">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">You keep</p>
            <p className="mt-2 text-[clamp(36px,5.5vw,80px)] font-extralight text-emerald-400 tabular-nums leading-none whitespace-nowrap">
              <BigNumberShort value={customerKeeps} />
            </p>
            <p className="mt-1 text-zinc-500 text-xs tabular-nums">${customerKeeps.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">QNNEAL earns</p>
            <p className="mt-2 text-[clamp(36px,5.5vw,80px)] font-extralight text-amber-400 tabular-nums leading-none whitespace-nowrap">
              <BigNumberShort value={qnnealRevenue} />
            </p>
            <p className="mt-1 text-zinc-500 text-xs tabular-nums">${qnnealRevenue.toLocaleString()}</p>
          </div>
        </div>
        <p className="text-zinc-400 text-[clamp(13px,1.4vw,18px)]">
          Only when we prove it. Audit-chained. Independently verifiable.
        </p>
        <Button
          data-testid="autopilot-pilot-cta"
          className="bg-amber-500 text-black hover:bg-amber-400 px-10 py-6 text-base font-medium tracking-wide"
        >
          Start 60-Day Pilot →
        </Button>
      </div>
    </SceneShell>
  );
}

// ─────────────────────────────────────────────────────────────────
// Scene scaffolding
// ─────────────────────────────────────────────────────────────────
function SceneShell({ label, children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16">
      <p className="absolute top-8 left-8 text-[10px] uppercase tracking-[0.4em] text-zinc-700">
        {label}
      </p>
      <div className="flex flex-col items-center text-center w-full">
        {children}
      </div>
    </div>
  );
}

function Subtitle({ children }) {
  return (
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.6 }}
      className="mt-10 text-zinc-400 text-[clamp(14px,1.8vw,24px)] max-w-3xl"
    >
      {children}
    </motion.p>
  );
}

// ─────────────────────────────────────────────────────────────────
// Scene engine
// ─────────────────────────────────────────────────────────────────
const SCENES = [
  { id: "spend",          duration: 8000,  Component: SpendScene },
  { id: "savings",        duration: 8000,  Component: SavingsScene },
  { id: "projection",     duration: 8000,  Component: ProjectionScene },
  { id: "live-tape",      duration: 10000, Component: LiveTapeScene },
  { id: "zero-knowledge", duration: 10000, Component: ZeroKnowledgeScene },
  { id: "tamper",         duration: 10000, Component: TamperScene },
  { id: "close",          duration: null,  Component: CloseScene }, // stays
];

export default function VistaAutopilot({ apiKey }) {
  const [data, setData] = useState({ overview: null, sim: null, proj: null });
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // ── Pre-load all 3 endpoints on mount (one-shot) ──
  useEffect(() => {
    let alive = true;
    const headers = { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" };
    (async () => {
      try {
        const [ov, sim, proj] = await Promise.all([
          fetch(`${BACKEND}/api/v1/demo/vista/portfolio-overview`, { headers }).then((r) => {
            if (!r.ok) throw new Error(`portfolio-overview: ${r.status}`);
            return r.json();
          }),
          fetch(`${BACKEND}/api/v1/demo/vista/simulate-savings`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              companies_to_simulate: 30,
              simulation_period_days: 30,
              show_per_company_breakdown: true,
              sample_calls_per_company: 250,
            }),
          }).then((r) => {
            if (!r.ok) throw new Error(`simulate-savings: ${r.status}`);
            return r.json();
          }),
          fetch(`${BACKEND}/api/v1/demo/vista/project-full-portfolio`, {
            method: "POST",
            headers,
          }).then((r) => {
            if (!r.ok) throw new Error(`project-full-portfolio: ${r.status}`);
            return r.json();
          }),
        ]);
        if (!alive) return;
        setData({ overview: ov, sim, proj });
        setLoaded(true);
      } catch (e) {
        if (alive) setError(e.message);
      }
    })();
    return () => { alive = false; };
  }, [apiKey]);

  // ── Auto-advance ──
  useEffect(() => {
    if (!loaded || paused) return;
    const scene = SCENES[sceneIdx];
    if (!scene.duration) return; // last scene stays
    const t = setTimeout(() => {
      setSceneIdx((i) => Math.min(SCENES.length - 1, i + 1));
    }, scene.duration);
    return () => clearTimeout(t);
  }, [sceneIdx, paused, loaded]);

  // ── Keyboard ──
  useEffect(() => {
    const h = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        setPaused((p) => !p);
      } else if (e.code === "ArrowLeft") {
        setSceneIdx((i) => Math.max(0, i - 1));
      } else if (e.code === "ArrowRight") {
        setSceneIdx((i) => Math.min(SCENES.length - 1, i + 1));
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-black text-red-400 flex items-center justify-center font-mono">
        Failed to load demo data: {error}
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="autopilot-loading">
        <motion.div
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className="text-amber-400 text-xs uppercase tracking-[0.4em]"
        >
          Pre-computing 7,500 quantum routing decisions…
        </motion.div>
      </div>
    );
  }

  const scene = SCENES[sceneIdx];

  return (
    <div className="min-h-screen bg-[#06070a] text-white relative overflow-hidden" data-testid="autopilot-root">
      {/* Subtle grain/gradient backdrop */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 30%, rgba(245,158,11,0.06), transparent 70%), radial-gradient(ellipse 50% 40% at 50% 100%, rgba(120,80,0,0.08), transparent 70%)",
        }}
      />

      {/* Scene viewport */}
      <AnimatePresence mode="wait">
        <motion.div
          key={scene.id}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -30 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10"
          data-testid={`autopilot-scene-${scene.id}`}
        >
          <scene.Component data={data} />
        </motion.div>
      </AnimatePresence>

      {/* Progress dots — bottom center, minimalist */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20" data-testid="autopilot-progress">
        {SCENES.map((s, i) => (
          <div
            key={s.id}
            className={`h-1 rounded-full transition-all duration-500 ${
              i === sceneIdx ? "w-12 bg-amber-400"
                : i < sceneIdx ? "w-6 bg-amber-500/50"
                : "w-6 bg-zinc-800"
            }`}
          />
        ))}
      </div>

      {/* Pause overlay */}
      <AnimatePresence>
        {paused && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute top-6 right-6 z-30 flex items-center gap-2 rounded-md border border-amber-500/40 bg-zinc-950/80 px-4 py-2 text-amber-400 text-sm font-mono backdrop-blur"
            data-testid="autopilot-paused"
          >
            ❙❙ paused · space to resume
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
